// "Pasar a 1080p" un borrador — ADR-2026-09-27-borrador-480p-y-pasar-a-1080p.
// El mismo camino que Corregir voz: POST que puede responder 202 y se pollea el job.
// Nunca manda un draft_id: el backend lo busca en la fila o en la execution del tenant.
import { atlasBackendFetch, pollJobUntilSettled } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';

export type ResultadoDelFinal =
  | { ok: true; videoId: string | null; path: string | null }
  | { ok: false; motivo: 'sin_creditos' | 'error'; mensaje: string };

// Cubre el tope del proveedor (~30 min) más el wait inicial de la ruta.
const POLL_MS = 30 * 60 * 1000;

export async function pasarBorradorA1080p(p: {
  videoId?: string;
  jobId?: string;
  /** El uuid del cliente para la fila nueva: quien lo pasa puede mostrar la generación en
   *  curso y retomarla tras una recarga (`pollGeneratedByExecutionId`). Sin él, se inventa. */
  executionId?: string;
}): Promise<ResultadoDelFinal> {
  let res: Response;
  try {
    res = await atlasBackendFetch('/v1/videos/borrador/finalizar', {
      ...(p.videoId ? { video_id: p.videoId } : {}),
      ...(p.jobId ? { job_id: p.jobId } : {}),
      execution_id: p.executionId ?? crypto.randomUUID(),
    });
  } catch {
    return { ok: false, motivo: 'error', mensaje: 'No se pudo conectar. Revisá tu conexión.' };
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 402) {
    return { ok: false, motivo: 'sin_creditos', mensaje: String(json.error ?? 'No te alcanzan los créditos.') };
  }
  if (res.status === 202) {
    const jobId = typeof json.job_id === 'string' ? json.job_id : null;
    if (!jobId) return { ok: false, motivo: 'error', mensaje: 'El servidor no devolvió el trabajo.' };
    const settled = await pollJobUntilSettled(jobId, { timeoutMs: POLL_MS, intervalMs: 5000 });
    if (!settled) {
      return {
        ok: false,
        motivo: 'error',
        mensaje: 'Tardó demasiado. El video puede aparecer igual en Creaciones en unos minutos.',
      };
    }
    if (settled.status === 'failed') {
      return { ok: false, motivo: 'error', mensaje: settled.userMessage || 'No pudimos pasar el video a 1080p.' };
    }
    return salida(settled.output ?? {});
  }
  if (!res.ok) {
    return { ok: false, motivo: 'error', mensaje: String(json.error ?? 'No pudimos pasar el video a 1080p.') };
  }
  return salida(json);
}

function salida(out: Record<string, unknown>): ResultadoDelFinal {
  return {
    ok: true,
    videoId: typeof out.video_id === 'string' ? out.video_id : null,
    path: typeof out.path === 'string' ? out.path : null,
  };
}

/**
 * La misma llamada como PROMESA DE TANDA (`GeneracionDelegada.resultado`): resuelve con
 * `[video_id]` del 1080p o rechaza con un `GenerateAdError` (billing si faltan créditos),
 * que es lo que las columnas de resultados y el lienzo ya saben mostrar.
 */
export function pasarBorradorA1080pComoTanda(p: {
  videoId?: string;
  jobId?: string;
  executionId: string;
}): Promise<string[]> {
  return pasarBorradorA1080p(p).then((r) => {
    if (!r.ok) throw new GenerateAdError(r.motivo === 'sin_creditos' ? 'billing' : 'technical', r.mensaje, r.motivo);
    if (!r.videoId) {
      throw new GenerateAdError('technical', 'El video salió pero no pudimos cargarlo. Mirá Creaciones.', 'sin_video_id');
    }
    return [r.videoId];
  });
}
